import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePushNotifications from '../hooks/usePushNotifications';
import { 
  Bell, BellRing, X, Check, CheckCheck, Package, Truck, 
  MessageSquare, ShoppingBag, AlertCircle, Trash2, Settings,
  BellOff, Loader2, Volume2, VolumeX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { playClientNotification, areSoundsEnabled, toggleSounds, initializeAudio } from '../utils/notificationSounds';

const NotificationIcon = ({ type }) => {
  const icons = {
    order_confirmed: { icon: ShoppingBag, color: 'text-green-500', bg: 'bg-green-50' },
    order_preparing: { icon: Package, color: 'text-blue-500', bg: 'bg-blue-50' },
    order_ready: { icon: Check, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    order_in_delivery: { icon: Truck, color: 'text-orange-500', bg: 'bg-orange-50' },
    order_delivered: { icon: CheckCheck, color: 'text-green-600', bg: 'bg-green-50' },
    order_cancelled: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
    new_message: { icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-50' },
  };
  
  const config = icons[type] || { icon: Bell, color: 'text-zinc-500', bg: 'bg-zinc-50' };
  const IconComponent = config.icon;
  
  return (
    <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center`}>
      <IconComponent className={`w-5 h-5 ${config.color}`} />
    </div>
  );
};

const NotificationBell = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(areSoundsEnabled());
  const previousUnreadRef = useRef(0);
  
  // Push notifications hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    toggle: togglePush
  } = usePushNotifications();

  // Initialize audio on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      initializeAudio();
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);
    return () => document.removeEventListener('click', handleFirstInteraction);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        api().get('/notifications'),
        api().get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data);
      const newUnreadCount = countRes.data.count;
      
      // Play sound if new notifications
      if (newUnreadCount > previousUnreadRef.current && soundsEnabled) {
        playClientNotification();
      }
      previousUnreadRef.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [api, user, soundsEnabled]);

  // Handle sound toggle
  const handleToggleSounds = () => {
    const newState = !soundsEnabled;
    setSoundsEnabled(newState);
    toggleSounds(newState);
    if (newState) {
      playClientNotification();
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api().patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api().patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Todas as notificações marcadas como lidas');
    } catch (err) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api().delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notificação eliminada');
    } catch (err) {
      toast.error('Erro ao eliminar notificação');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    if (notification.order_id) {
      navigate(`/order/${notification.order_id}`);
    }
    setShowDropdown(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Agora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('pt-AO', { day: '2-digit', month: '2-digit' });
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowDropdown(!showDropdown);
          setShowSettings(false);
          if (!showDropdown) fetchNotifications();
        }}
        className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
        data-testid="notification-bell"
      >
        {pushSubscribed ? (
          <BellRing className="w-5 h-5 text-white" />
        ) : (
          <Bell className="w-5 h-5 text-white" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-sonangol-red text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-zinc-200 z-50 max-h-[70vh] overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="font-barlow font-bold text-lg">Notificações</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-sonangol-red hover:underline"
                    data-testid="mark-all-read"
                  >
                    Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 ${showSettings ? 'bg-zinc-100' : ''}`}
                  data-testid="notification-settings-btn"
                >
                  <Settings className="w-4 h-4 text-zinc-500" />
                </button>
                <button
                  onClick={() => setShowDropdown(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100 space-y-3">
                {/* Sound Settings */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${soundsEnabled ? 'bg-green-100' : 'bg-zinc-200'}`}>
                      {soundsEnabled ? (
                        <Volume2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <VolumeX className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-800">Som de Notificação</p>
                      <p className="text-xs text-zinc-500">
                        {soundsEnabled ? 'Activado' : 'Desactivado'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleSounds}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      soundsEnabled 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
                    }`}
                  >
                    {soundsEnabled ? 'Ligado' : 'Desligado'}
                  </button>
                </div>

                {/* Push Settings */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${pushSubscribed ? 'bg-green-100' : 'bg-zinc-200'}`}>
                      {pushSubscribed ? (
                        <BellRing className="w-5 h-5 text-green-600" />
                      ) : (
                        <BellOff className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-800">Notificações Push</p>
                      <p className="text-xs text-zinc-500">
                        {!pushSupported ? 'Não suportado' : 
                         pushPermission === 'denied' ? 'Bloqueado pelo navegador' :
                         pushSubscribed ? 'Activadas' : 'Desactivadas'}
                      </p>
                    </div>
                  </div>
                  {pushSupported && pushPermission !== 'denied' && (
                    <button
                      onClick={togglePush}
                      disabled={pushLoading}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        pushSubscribed 
                          ? 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
                          : 'bg-sonangol-red text-white hover:bg-red-700'
                      }`}
                      data-testid="toggle-push-btn"
                    >
                      {pushLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : pushSubscribed ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </div>
                {pushPermission === 'denied' && (
                  <p className="text-xs text-orange-600 mt-2">
                    Para activar, vá às definições do navegador e permita notificações para este site.
                  </p>
                )}
              </div>
            )}

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[50vh]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">Sem notificações</p>
                  {!pushSubscribed && pushSupported && (
                    <button
                      onClick={togglePush}
                      className="mt-3 text-xs text-sonangol-red hover:underline"
                    >
                      Activar notificações push
                    </button>
                  )}
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 transition-colors group ${
                      !notification.is_read ? 'bg-red-50/30' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <NotificationIcon type={notification.notification_type} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notification.is_read ? 'text-zinc-900' : 'text-zinc-700'}`}>
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => deleteNotification(notification.id, e)}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3 text-zinc-400" />
                          </button>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-zinc-400">
                            {formatTime(notification.created_at)}
                          </span>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-sonangol-red rounded-full"></span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-3 border-t border-zinc-100">
                <Button
                  variant="ghost"
                  className="w-full text-sm text-zinc-600 hover:text-sonangol-red"
                  onClick={() => {
                    navigate('/orders');
                    setShowDropdown(false);
                  }}
                >
                  Ver todos os pedidos
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
